import { useState, useEffect } from 'react';
import axios from 'axios';
import ArtworkCard from '../components/ArtworkCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Portfolio = () => {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [activeTab, setActiveTab] = useState('available');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        const response = await axios.get(`${API}/artworks`);
        setArtworks(response.data);
      } catch (error) {
        console.error('Error fetching artworks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtworks();
  }, []);

  // Reset image index when selecting new artwork
  const handleSelectArtwork = (artwork) => {
    setSelectedArtwork(artwork);
    setCurrentImageIndex(0);
    // Track the view
    axios.post(`${API}/analytics/track/${artwork.id}`).catch(() => {});
  };

  // Get all images for the selected artwork
  const getArtworkImages = (artwork) => {
    if (!artwork) return [];
    if (artwork.images && artwork.images.length > 0) {
      return artwork.images;
    }
    return artwork.image_url ? [artwork.image_url] : [];
  };

  const images = selectedArtwork ? getArtworkImages(selectedArtwork) : [];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Filter artworks by status
  const availableArtworks = artworks.filter(a => a.status === 'available' || !a.status);
  const pastWorks = artworks.filter(a => a.status === 'sold');

  const displayedArtworks = activeTab === 'available' ? availableArtworks : pastWorks;

  return (
    <div className="min-h-screen pt-20 bg-black" data-testid="portfolio-page">
      {/* Header */}
      <section className="py-16 lg:py-24 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-teal-400 uppercase tracking-[0.3em] text-xs font-medium mb-4">
            Stone on Glass
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Portfolio
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto mb-10">
            A collection of stone on glass artworks, each piece uniquely crafted 
            from natural stones collected from the beaches of Victoria, BC.
          </p>

          {/* Category Tabs */}
          <div className="flex justify-center gap-4" data-testid="portfolio-tabs">
            <button
              onClick={() => setActiveTab('available')}
              className={`px-8 py-3 rounded-sm uppercase tracking-widest text-xs font-semibold transition-all duration-300 ${
                activeTab === 'available'
                  ? 'bg-teal-500 text-black'
                  : 'border border-white/30 text-white hover:bg-white/10'
              }`}
              data-testid="tab-available"
            >
              Available ({availableArtworks.length})
            </button>
            <button
              onClick={() => setActiveTab('past')}
              className={`px-8 py-3 rounded-sm uppercase tracking-widest text-xs font-semibold transition-all duration-300 ${
                activeTab === 'past'
                  ? 'bg-teal-500 text-black'
                  : 'border border-white/30 text-white hover:bg-white/10'
              }`}
              data-testid="tab-past"
            >
              Past Works ({pastWorks.length})
            </button>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 lg:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" data-testid="loading-spinner" />
            </div>
          ) : displayedArtworks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/50">
                {activeTab === 'available' 
                  ? 'No available artworks at the moment. Check back soon!' 
                  : 'No past works to display.'}
              </p>
            </div>
          ) : (
            <div className="masonry-grid" data-testid="artwork-grid">
              {displayedArtworks.map((artwork) => (
                <ArtworkCard
                  key={artwork.id}
                  artwork={artwork}
                  onClick={handleSelectArtwork}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Artwork Detail Modal with Image Gallery */}
      <Dialog open={!!selectedArtwork} onOpenChange={() => setSelectedArtwork(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0 bg-neutral-900 border-white/10" data-testid="artwork-modal">
          {selectedArtwork && (
            <>
              <button
                onClick={() => setSelectedArtwork(null)}
                className="absolute top-4 right-4 z-20 p-2 bg-black/80 backdrop-blur-sm rounded-full hover:bg-black transition-colors"
                data-testid="modal-close-btn"
              >
                <X className="h-5 w-5 text-white" />
              </button>
              
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Image Gallery */}
                <div className="relative bg-black min-h-[300px] lg:min-h-[500px] flex items-center justify-center">
                  <img
                    src={images[currentImageIndex]}
                    alt={`${selectedArtwork.title} - Image ${currentImageIndex + 1}`}
                    className="w-full h-full object-contain max-h-[500px]"
                    data-testid="modal-image"
                  />
                  
                  {/* Navigation arrows - only show if multiple images */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 rounded-full transition-colors"
                        data-testid="prev-image-btn"
                      >
                        <ChevronLeft className="h-6 w-6 text-white" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 rounded-full transition-colors"
                        data-testid="next-image-btn"
                      >
                        <ChevronRight className="h-6 w-6 text-white" />
                      </button>
                      
                      {/* Image counter */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-3 py-1 rounded-full">
                        {currentImageIndex + 1} / {images.length}
                      </div>
                    </>
                  )}
                  
                  {/* Thumbnail strip */}
                  {images.length > 1 && (
                    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-2">
                      {images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`w-12 h-12 rounded-sm overflow-hidden border-2 transition-all ${
                            idx === currentImageIndex 
                              ? 'border-teal-400 opacity-100' 
                              : 'border-transparent opacity-50 hover:opacity-75'
                          }`}
                          data-testid={`thumbnail-${idx}`}
                        >
                          <img 
                            src={img} 
                            alt={`Thumbnail ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="p-8 lg:p-12">
                  <DialogHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <p className="text-teal-400 uppercase tracking-[0.2em] text-xs font-medium">
                        {selectedArtwork.category || 'Public Art'}
                      </p>
                      {selectedArtwork.status === 'sold' && (
                        <span className="bg-white/10 text-white/60 text-xs px-2 py-1 rounded-sm uppercase tracking-wider">
                          Sold
                        </span>
                      )}
                    </div>
                    <DialogTitle className="font-heading text-3xl font-semibold text-white mb-4">
                      {selectedArtwork.title}
                    </DialogTitle>
                  </DialogHeader>

                  <div className="space-y-6 mt-6">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-white/50 mb-1">
                        Dimensions
                      </p>
                      <p className="text-white font-medium">
                        {selectedArtwork.dimensions}
                      </p>
                    </div>

                    {selectedArtwork.year && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/50 mb-1">
                          Year
                        </p>
                        <p className="text-white font-medium">
                          {selectedArtwork.year}
                        </p>
                      </div>
                    )}

                    {selectedArtwork.description && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/50 mb-2">
                          About This Piece
                        </p>
                        <p className="text-white/70 leading-relaxed">
                          {selectedArtwork.description}
                        </p>
                      </div>
                    )}

                    <div className="pt-6 border-t border-white/10">
                      {selectedArtwork.status === 'sold' ? (
                        <p className="text-white/50 text-sm">
                          This piece has been sold. {' '}
                          <a 
                            href="mailto:richardsonjim@gmail.com" 
                            className="text-teal-400 hover:text-teal-300 underline"
                            data-testid="modal-contact-link"
                          >
                            Contact Jim
                          </a>
                          {' '} for commission inquiries.
                        </p>
                      ) : (
                        <p className="text-white/50 text-sm">
                          Interested in this piece? {' '}
                          <a 
                            href="mailto:richardsonjim@gmail.com" 
                            className="text-teal-400 hover:text-teal-300 underline"
                            data-testid="modal-contact-link"
                          >
                            Contact Jim
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Portfolio;
